import * as React from 'react';
import {
  HealthProfilePageWrapper,
  MoreOptionsCard,
  MoreOptionsLink,
} from '@leagueplatform/health-profile-common';
import { useIntl } from '@leagueplatform/locales';
import { Sidebar } from '@leagueplatform/web-common-components';
import { track } from '@leagueplatform/analytics';
import { Box } from '@leagueplatform/genesis-core';
import { HealthLiteracyContent } from '#components/health-literacy/health-literacy-content.tsx';
import { AddEditDataModal } from '#components/add-edit-data/add-edit-data-modal.tsx';
import { AddEditDataManagedModal } from '#components/add-edit-data/add-edit-data-managed-modal.tsx';
import { useIsSelfReportedDataEnabled } from '#hooks/use-is-self-reported-data-enabled.hook.ts';
import { useHealthMetricDocumentTitle } from '#hooks/use-health-metric-document-title.ts';
import { useParamsLegacy as useParams } from '@leagueplatform/routing';
import { UnavailableDeviceConnectModal } from '@leagueplatform/health-programs';
import { DEFAULT_NAMESPACE } from '@leagueplatform/masonry-engine';
import { useChartData } from './hooks/use-chart-data';
import { AddMetricDataButton } from '../details3/components/add-metric-data-button';
import { HealthPrograms } from '../details3/components/health-programs';
import {
  ABOUT_ME_METRIC_DETAILS_ROOT_NODE_ID,
  useAboutMeMetricDetailsMasonryEngineInstance,
} from './hooks/use-about-me-metric-details-masonry-engine-instance';

const handleAnalytics = ({
  metricType,
  isEmptyState,
}: {
  metricType?: string;
  isEmptyState?: boolean;
}) =>
  track('dashboard.screen_loaded.metric_item_metric_type', {
    metric_type: metricType || '',
    metric_id: '',
    is_empty_state: isEmptyState ? String(isEmptyState) : '',
  });

type Params = {
  dataType: string;
};

export const Details = () => {
  const { dataType } = useParams<Params>();
  const { isSelfReportedDataEnabled } = useIsSelfReportedDataEnabled();
  const pageViewAnalyticsRef = React.useRef(false);
  const { formatMessage } = useIntl();
  const [addEditDataModalOpened, setAddEditDataModalOpened] =
    React.useState(false);
  const [displayUnavailableOnWebModal, setDisplayUnavailableOnWebModal] =
    React.useState(false);
  const MasonryEngineInstance = useAboutMeMetricDetailsMasonryEngineInstance({
    metricType: dataType,
    onAddDataClick: () => {
      setAddEditDataModalOpened(true);
    },
    onConnectDeviceClick: () => {
      setDisplayUnavailableOnWebModal(true);
    },
  });

  const chartDataQuery = useChartData({
    metric: dataType,
    from: new Date(),
  });

  const chartData = chartDataQuery?.data?.data[0];
  const meta = chartDataQuery?.data?.meta;
  const isAnyDataAvailable = !!meta?.isAnyDataAvailable;
  const metricDisplayName = chartData?.metricDisplayName;

  useHealthMetricDocumentTitle(
    formatMessage({
      id: 'METRIC_DETAILS',
    }),
    metricDisplayName,
  );

  React.useEffect(() => {
    if (metricDisplayName && !pageViewAnalyticsRef.current) {
      handleAnalytics({
        metricType: dataType,
        isEmptyState: !isAnyDataAvailable,
      });
      pageViewAnalyticsRef.current = true;
    }
  }, [dataType, metricDisplayName, isAnyDataAvailable]);

  const showAddDataButton =
    chartData &&
    chartData.metricComponents.data.some(
      (config) => config.sourceType === 'self_reported',
    ) &&
    isSelfReportedDataEnabled &&
    chartData.addInputConfig;

  return (
    <HealthProfilePageWrapper
      title={metricDisplayName || formatMessage({ id: 'METRIC_DETAILS' })}
      sidebar={
        <Sidebar>
          {isAnyDataAvailable ? (
            <>
              <MoreOptionsCard>
                <MoreOptionsLink
                  to="all-recorded-data"
                  onClick={() =>
                    track(
                      'dashboard.button_clicked.metric_item_metric_type.navigate_to_all_recorded_data',
                      {
                        metric_type: dataType,
                        metric_id: '',
                        metric_status: '',
                        content_id: '',
                        content_name: '',
                      },
                    )
                  }
                >
                  {formatMessage(
                    { id: 'VIEW_ALL_METRIC_DATA' },
                    {
                      metric: metricDisplayName,
                    },
                  )}
                </MoreOptionsLink>
                <MoreOptionsLink
                  to="data-sources"
                  onClick={() =>
                    track(
                      'dashboard.button_clicked.metric_item_options_modal_metric_type.navigate_to_all_data_sources',
                      {
                        metric_type: dataType,
                        metric_id: '',
                        metric_status: '',
                        content_id: '',
                        content_name: 'view all data sources',
                        contentful_system: '',
                      },
                    )
                  }
                >
                  {formatMessage({ id: 'VIEW_DATA_SOURCES' })}
                </MoreOptionsLink>
              </MoreOptionsCard>
              {showAddDataButton ? (
                <AddEditDataModal
                  metricConfigId={chartData.id}
                  inputConfig={chartData.addInputConfig.data}
                  onAddData={() => {
                    MasonryEngineInstance.invokeAction({
                      type: 'reload',
                      namespace: DEFAULT_NAMESPACE,
                      payload: {
                        nodeIds: [ABOUT_ME_METRIC_DETAILS_ROOT_NODE_ID],
                      },
                    });
                  }}
                  modalTrigger={
                    <AddMetricDataButton
                      onClick={() =>
                        track(
                          'dashboard.button_clicked.metric_item_metric_type.add_data',
                          {
                            metric_type: dataType,
                            metric_id: '',
                            is_empty_state: String(!isAnyDataAvailable),
                            content_id: '',
                            contentful_system: '',
                          },
                        )
                      }
                    />
                  }
                />
              ) : null}
            </>
          ) : null}
        </Sidebar>
      }
    >
      <Box
        css={{
          '.GDS-vertical-stack-layout': {
            height: 'fit-content',
          },
        }}
      >
        <MasonryEngineInstance.Component />
      </Box>
      <HealthPrograms metric={dataType} />
      <HealthLiteracyContent metric={dataType} />
      {showAddDataButton ? (
        <AddEditDataManagedModal
          opened={addEditDataModalOpened}
          onClose={() => {
            setAddEditDataModalOpened(false);
          }}
          metricConfigId={chartData.id}
          inputConfig={chartData.addInputConfig.data}
        />
      ) : null}
      {displayUnavailableOnWebModal ? (
        <UnavailableDeviceConnectModal
          onClose={() => setDisplayUnavailableOnWebModal(false)}
        />
      ) : null}
    </HealthProfilePageWrapper>
  );
};
